import {DraftsOutlined, MarkEmailUnreadOutlined, NotificationsNoneOutlined} from '@mui/icons-material';
import {FC} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {NotificationStats} from 'src/types/stats';

/** Which notifications the list shows. One axis: picking one replaces the other, and All is the way back. */
export type NotificationStatus = 'all' | 'unread' | 'read';

/** The list's `read` filter for a status - none at all for All. */
export const readFlagOf = (status: NotificationStatus): boolean | undefined =>
    status === 'all' ? undefined : status === 'read';

interface NotificationStatsStripProps {
    data?: NotificationStats;
    activeStatus: NotificationStatus;
    onSelectStatus: (status: NotificationStatus) => void;
}

/**
 * The block every list opens with, for notifications: how many there are, how many are still waiting
 * to be read and how many have been.
 */
export const NotificationStatsStrip: FC<NotificationStatsStripProps> = ({data, activeStatus, onSelectStatus}) => {
    const intl = useIntl();

    return (
        <StatsStrip
            icon={<NotificationsNoneOutlined/>}
            title="notifications"
            subtitle={intl.formatMessage(
                {id: 'notificationStatsSubtitle'},
                {notifications: data?.notifications ?? 0}
            )}
            tiles={[
                {
                    key: 'all',
                    label: 'notificationsAll',
                    value: data?.notifications,
                    icon: <NotificationsNoneOutlined/>,
                    tone: 'primary',
                    active: activeStatus === 'all',
                    onClick: () => onSelectStatus('all'),
                },
                {
                    key: 'unread',
                    label: 'notificationsUnread',
                    value: data?.unread,
                    icon: <MarkEmailUnreadOutlined/>,
                    // Waiting for somebody, not wrong: the tone draws the eye without calling it a fault.
                    tone: 'warning',
                    active: activeStatus === 'unread',
                    onClick: () => onSelectStatus('unread'),
                },
                {
                    key: 'read',
                    label: 'notificationsRead',
                    value: data?.read,
                    icon: <DraftsOutlined/>,
                    tone: 'neutral',
                    active: activeStatus === 'read',
                    onClick: () => onSelectStatus('read'),
                },
            ]}
        />
    );
};
